import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import {
  Mail, Send, Inbox, FileText, ArrowLeft, Loader2, Check, X, ExternalLink,
  Paperclip, Clock, User, ChevronRight, Plus, RefreshCw, AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const EmailIntegration = () => {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [showCompose, setShowCompose] = useState(false);
  const [composeData, setComposeData] = useState({ to: '', cc: '', subject: '', body: '' });
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const checkConnection = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/microsoft/status`, { withCredentials: true });
      setConnectionStatus(data);
      if (data.connected) {
        fetchMessages('inbox');
        fetchTemplates();
      }
    } catch (err) {
      console.error('Failed to check Microsoft connection:', err);
      setConnectionStatus({ connected: false });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkConnection();
    
    // Check for connection callback params
    const params = new URLSearchParams(window.location.search);
    if (params.get('ms_connected') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      checkConnection();
    }
  }, [checkConnection]);

  const fetchMessages = async (folder) => {
    setLoadingMessages(true);
    setActiveFolder(folder);
    try {
      const { data } = await axios.get(`${API}/api/emails/messages`, {
        params: { folder, top: 50 },
        withCredentials: true
      });
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      setMessages([]);
    }
    setLoadingMessages(false);
  };

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/email-templates`, { withCredentials: true });
      setTemplates(data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  };

  const connectMicrosoft = async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/microsoft/login`, { withCredentials: true });
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      }
    } catch (err) {
      console.error('Failed to start Microsoft auth:', err);
      alert(err.response?.data?.detail || 'Failed to connect Microsoft account');
    }
  };

  const disconnectMicrosoft = async () => {
    if (!window.confirm('Disconnect your Microsoft email account?')) return;
    try {
      await axios.delete(`${API}/api/auth/microsoft/disconnect`, { withCredentials: true });
      setConnectionStatus({ connected: false });
      setMessages([]);
    } catch (err) {
      console.error('Failed to disconnect:', err);
    }
  };

  const sendEmail = async () => {
    if (!composeData.to.trim() || !composeData.subject.trim()) return;
    setSending(true);
    try {
      const toList = composeData.to.split(',').map(e => e.trim()).filter(Boolean);
      const ccList = composeData.cc ? composeData.cc.split(',').map(e => e.trim()).filter(Boolean) : [];
      
      await axios.post(`${API}/api/emails/send`, {
        to: toList,
        cc: ccList.length > 0 ? ccList : undefined,
        subject: composeData.subject,
        body: composeData.body,
        is_html: false
      }, { withCredentials: true });
      
      setComposeData({ to: '', cc: '', subject: '', body: '' });
      setShowCompose(false);
      fetchMessages('sent');
    } catch (err) {
      console.error('Failed to send email:', err);
      alert(err.response?.data?.detail || 'Failed to send email');
    }
    setSending(false);
  };

  const applyTemplate = (template) => {
    setComposeData(prev => ({
      ...prev,
      subject: template.subject,
      body: template.body
    }));
    setShowTemplateModal(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ padding: '3rem' }}>
        <Loader2 size={32} className="animate-spin" style={{ color: 'var(--teal)' }} />
      </div>
    );
  }

  // Not connected - show connection UI
  if (!connectionStatus?.connected) {
    return (
      <div data-testid="email-connect-screen" style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{
          width: 80, height: 80, margin: '0 auto 1.5rem', borderRadius: '50%',
          background: 'linear-gradient(135deg, #0078D4 0%, #106EBE 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Mail size={36} style={{ color: '#fff' }} />
        </div>
        <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy)', marginBottom: '0.5rem' }}>
          Connect Your Microsoft Email
        </h2>
        <p style={{ fontSize: '0.875rem', color: '#6B7280', maxWidth: 400, margin: '0 auto 1.5rem' }}>
          Connect your Microsoft 365 or Outlook account to send and receive emails directly from the CRM.
        </p>
        <Button onClick={connectMicrosoft} className="btn-primary" data-testid="connect-microsoft-btn">
          <ExternalLink size={16} className="mr-2" />
          Connect Microsoft Account
        </Button>
        <p style={{ fontSize: '0.7rem', color: '#9CA3AF', marginTop: '1rem' }}>
          Your credentials are securely encrypted and never stored in plain text.
        </p>
      </div>
    );
  }

  // Compose email view
  if (showCompose) {
    return (
      <div data-testid="email-compose" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{
          padding: '0.75rem 1rem', borderBottom: '1px solid #E5E7EB',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button onClick={() => setShowCompose(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
              <ArrowLeft size={20} />
            </button>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>
              New Email
            </h3>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button variant="outline" size="sm" onClick={() => setShowTemplateModal(true)}>
              <FileText size={14} className="mr-1" /> Templates
            </Button>
            <Button onClick={sendEmail} disabled={sending || !composeData.to.trim()} className="btn-primary" size="sm" data-testid="send-email-btn">
              {sending ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
              Send
            </Button>
          </div>
        </div>
        
        <div style={{ flex: 1, padding: '1rem', overflowY: 'auto' }}>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
              From
            </label>
            <div style={{ fontSize: '0.875rem', color: 'var(--navy)', padding: '0.5rem', background: '#F9FAFB', borderRadius: 6 }}>
              {connectionStatus.email}
            </div>
          </div>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
              To (comma-separated)
            </label>
            <Input
              value={composeData.to}
              onChange={(e) => setComposeData({ ...composeData, to: e.target.value })}
              placeholder="recipient@example.com"
              data-testid="email-to-input"
              style={{ background: '#fff', border: '1px solid #E5E7EB', color: 'var(--navy)' }}
            />
          </div>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
              CC (optional)
            </label>
            <Input
              value={composeData.cc}
              onChange={(e) => setComposeData({ ...composeData, cc: e.target.value })}
              placeholder="cc@example.com"
              style={{ background: '#fff', border: '1px solid #E5E7EB', color: 'var(--navy)' }}
            />
          </div>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
              Subject
            </label>
            <Input
              value={composeData.subject}
              onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
              placeholder="Email subject"
              data-testid="email-subject-input"
              style={{ background: '#fff', border: '1px solid #E5E7EB', color: 'var(--navy)' }}
            />
          </div>
          
          <div>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
              Message
            </label>
            <textarea
              value={composeData.body}
              onChange={(e) => setComposeData({ ...composeData, body: e.target.value })}
              placeholder="Type your message..."
              data-testid="email-body-input"
              rows={12}
              style={{
                width: '100%', padding: '0.75rem', borderRadius: 6, border: '1px solid #E5E7EB',
                fontSize: '0.875rem', color: 'var(--navy)', resize: 'vertical', fontFamily: 'var(--font-body)'
              }}
            />
          </div>
        </div>

        {/* Template Modal */}
        {showTemplateModal && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <div style={{
              background: '#fff', borderRadius: 12, padding: '1.5rem', width: 400, maxWidth: '90vw',
              maxHeight: '70vh', overflow: 'auto'
            }}>
              <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>
                Email Templates
              </h3>
              {templates.length === 0 ? (
                <p style={{ color: '#9CA3AF', fontSize: '0.875rem' }}>No templates yet</p>
              ) : templates.map(t => (
                <div
                  key={t.id}
                  onClick={() => applyTemplate(t)}
                  style={{
                    padding: '0.75rem', borderRadius: 6, border: '1px solid #E5E7EB',
                    marginBottom: '0.5rem', cursor: 'pointer'
                  }}
                >
                  <div style={{ fontWeight: 500, color: 'var(--navy)', fontSize: '0.875rem' }}>{t.name}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>{t.subject}</div>
                </div>
              ))}
              <Button onClick={() => setShowTemplateModal(false)} variant="outline" style={{ marginTop: '1rem' }}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Email list view
  return (
    <div data-testid="email-inbox" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        padding: '0.75rem 1rem', borderBottom: '1px solid #E5E7EB',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%', background: '#0078D4',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Mail size={16} style={{ color: '#fff' }} />
          </div>
          <div>
            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--navy)' }}>{connectionStatus.display_name}</div>
            <div style={{ fontSize: '0.7rem', color: '#9CA3AF' }}>{connectionStatus.email}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button variant="outline" size="sm" onClick={() => fetchMessages(activeFolder)}>
            <RefreshCw size={14} />
          </Button>
          <Button onClick={() => setShowCompose(true)} className="btn-primary" size="sm" data-testid="compose-email-btn">
            <Plus size={14} className="mr-1" /> Compose
          </Button>
        </div>
      </div>

      {/* Folder Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB' }}>
        {[
          { id: 'inbox', label: 'Inbox', icon: Inbox },
          { id: 'sent', label: 'Sent', icon: Send }
        ].map(folder => (
          <button
            key={folder.id}
            onClick={() => fetchMessages(folder.id)}
            data-testid={`folder-${folder.id}`}
            style={{
              flex: 1, padding: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
              fontSize: '0.813rem', fontWeight: activeFolder === folder.id ? 600 : 400,
              color: activeFolder === folder.id ? 'var(--teal)' : '#6B7280',
              background: 'none', border: 'none', borderBottom: activeFolder === folder.id ? '2px solid var(--teal)' : '2px solid transparent',
              cursor: 'pointer'
            }}
          >
            <folder.icon size={14} />
            {folder.label}
          </button>
        ))}
      </div>

      {/* Messages List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loadingMessages ? (
          <div className="flex items-center justify-center" style={{ padding: '2rem' }}>
            <Loader2 size={24} className="animate-spin" style={{ color: 'var(--teal)' }} />
          </div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#9CA3AF' }}>
            No messages in {activeFolder}
          </div>
        ) : messages.map((msg, i) => (
          <div
            key={msg.id || i}
            data-testid={`email-row-${i}`}
            style={{
              padding: '0.75rem 1rem', borderBottom: '1px solid #F3F4F6', cursor: 'pointer',
              background: msg.is_read === false ? '#F0F9FF' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', background: '#E5E7EB',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <User size={16} style={{ color: '#6B7280' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.125rem' }}>
                  <span style={{
                    fontWeight: msg.is_read === false ? 600 : 400, fontSize: '0.875rem', color: 'var(--navy)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>
                    {msg.from_name || msg.from_email || 'Unknown'}
                  </span>
                  <span style={{ fontSize: '0.7rem', color: '#9CA3AF', flexShrink: 0 }}>
                    {formatDate(msg.received_at)}
                  </span>
                </div>
                <div style={{
                  fontWeight: msg.is_read === false ? 600 : 400, fontSize: '0.813rem', color: 'var(--navy)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '0.125rem'
                }}>
                  {msg.subject || '(No Subject)'}
                </div>
                <div style={{
                  fontSize: '0.75rem', color: '#9CA3AF',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                  {msg.preview || ''}
                </div>
                {msg.has_attachments && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem' }}>
                    <Paperclip size={12} style={{ color: '#9CA3AF' }} />
                    <span style={{ fontSize: '0.65rem', color: '#9CA3AF' }}>Attachment</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ padding: '0.5rem 1rem', borderTop: '1px solid #E5E7EB', background: '#FAFBFC' }}>
        <button
          onClick={disconnectMicrosoft}
          style={{
            fontSize: '0.7rem', color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer'
          }}
        >
          Disconnect Microsoft Account
        </button>
      </div>
    </div>
  );
};

export default EmailIntegration;
